import React, { useMemo, useEffect } from "react";
import { Modal } from "@mui/material";
import { useForm } from "react-hook-form";
import { XIcon } from "@heroicons/react/solid";
import { Button, Input, DateInput, Select } from "../../components/ui";
import InlineLoader from "../../components/loader/InlineLoader";
import useGet from "../../hooks/read/useGet";

const FundRequestForm = ({
  open,
  onClose,
  editRequest,
  isCreating,
  isUpdating,
  isSettingsLoading,
  priorNoDays = 0,
  onSubmit,
}) => {
  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      from_date: "",
      to_date: "",
      currency_type_id: "",
      entity_id: "",
      requested_amount: "",
      remarks: "",
    },
  });

  // Fetch currencies
  const { list: currencyList } = useGet({
    url: "petty-cash/currency_list",
    initialData: { limit: 50 },
  });

  // Fetch entities
  const { list: entityList } = useGet({
    url: "petty-cash/entity_list",
    initialData: { limit: 50 },
  });

  const watchedFromDate = watch("from_date");

  const minAllowedDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - priorNoDays);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [priorNoDays]);

  useEffect(() => {
    if (open && editRequest) {
      reset({
        from_date: editRequest.from_date || "",
        to_date: editRequest.to_date || "",
        currency_type_id: editRequest.currency_type_id ? String(editRequest.currency_type_id) : "",
        entity_id: editRequest.entity_id ? String(editRequest.entity_id) : "",
        requested_amount: editRequest.requested_amount || "",
        remarks: editRequest.remarks || "",
      });
    } else if (open && !editRequest) {
      reset({
        from_date: "",
        to_date: "",
        currency_type_id: "",
        entity_id: "",
        requested_amount: "",
        remarks: "",
      });
    }
  }, [editRequest, reset, open]);

  // Set default currency if only 1 currency available
  useEffect(() => {
    const currencies = currencyList.data?.data || [];
    if (currencies.length === 1 && !watch("currency_type_id")) {
      setValue("currency_type_id", String(currencies[0].currency_type_id));
    }
  }, [currencyList.data, setValue, watch]);

  const currencyOptions = useMemo(() => {
    const items = currencyList.data?.data || [];
    return items.map((c) => ({
      value: String(c.currency_type_id),
      label: `${c.currency_type} (${c.currency_symbol})`,
    }));
  }, [currencyList.data]);

  const entityOptions = useMemo(() => {
    const items = entityList.data?.data || [];
    return items.map((e) => ({
      value: String(e.entity_id),
      label: e.name,
    }));
  }, [entityList.data]);

  const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    return new Date(value);
  };

  const dateRangeValidate = (value, formValues) => {
    if (!value || !formValues.to_date) return true;
    const from = parseDate(value);
    const to = parseDate(formValues.to_date);
    if (!from || !to) return true;
    return from <= to || "From date must be on or before to date";
  };

  const handleFormSubmit = (data) => {
    const payload = {
      from_date:
        data.from_date instanceof Date
          ? data.from_date.toISOString().split("T")[0]
          : data.from_date,
      to_date:
        data.to_date instanceof Date
          ? data.to_date.toISOString().split("T")[0]
          : data.to_date,
      currency_type_id: data.currency_type_id || "",
      entity_id: data.entity_id || "",
      requested_amount: data.requested_amount,
      remarks: data.remarks || "",
    };
    if (editRequest) {
      payload.request_id = editRequest.request_id;
    }
    onSubmit(payload);
  };

  const isSaving = isCreating || isUpdating;
  const disableForm = isSettingsLoading || isSaving;

  return (
    <Modal open={open} onClose={disableForm ? undefined : onClose}>
      <div className="w-full h-full md:h-auto md:w-[90%] lg:w-[60%] max-w-lg bg-white rounded-none md:rounded-2xl shadow-2xl absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 overflow-hidden border-0 md:border md:border-gray-150 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-bold text-gray-800">
            {editRequest ? "Edit Fund Request" : "New Fund Request"}
          </h3>
          <button
            onClick={onClose}
            disabled={disableForm}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-4 max-h-[85vh] overflow-y-auto">
          {priorNoDays > 0 && (
            <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              From date cannot be earlier than {priorNoDays} day{priorNoDays > 1 ? "s" : ""} prior.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DateInput
              control={control}
              label="From Date"
              name="from_date"
              minDate={minAllowedDate}
              rules={{
                required: "From date is required",
                validate: (value, formValues) => dateRangeValidate(value, formValues),
              }}
            />
            <DateInput
              control={control}
              label="To Date"
              name="to_date"
              minDate={watchedFromDate ? parseDate(watchedFromDate) : minAllowedDate}
              rules={{
                required: "To date is required",
                validate: (value, formValues) => {
                  if (!value || !formValues.from_date) return true;
                  const from = parseDate(formValues.from_date);
                  const to = parseDate(value);
                  if (!from || !to) return true;
                  return from <= to || "To date must be on or after from date";
                },
              }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              control={control}
              label="Currency"
              name="currency_type_id"
              rules={{ required: "Currency is required" }}
              options={currencyOptions}
            />
            <Select
              control={control}
              label="Entity (Vendor/Merchant)"
              name="entity_id"
              options={entityOptions}
            />
          </div>

          <Input
            control={control}
            label="Requested Amount"
            name="requested_amount"
            type="number"
            isCurrency={true}
            placeholder="0.00"
            rules={{
              required: "Amount is required",
              min: { value: 0.01, message: "Amount must be greater than 0" },
            }}
          />
          <Input
            control={control}
            label="Remarks"
            name="remarks"
            placeholder="Enter remarks (optional)"
            isTextarea={true}
          />
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={disableForm}>
              Cancel
            </Button>
            <Button type="submit" disabled={disableForm}>
              {isSettingsLoading ? (
                <>
                  <span>Loading settings...</span>
                  <InlineLoader className="ml-2" />
                </>
              ) : isSaving ? (
                <>
                  <span>Saving...</span>
                  <InlineLoader className="ml-2" />
                </>
              ) : editRequest ? (
                "Update"
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default FundRequestForm;
